import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ModalBody,
  Modal,
  Form,
  Nav,
  TabContent,
  TabPane,
  Table,
  Spinner, Alert,
} from "reactstrap";
import Select from "react-select";
import api, { handleApiError } from "../../../../helpers/api";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { useSelector, useDispatch } from "react-redux";
import { fetchJobsRequest, updateJobRequest } from "../../../../store/job/action";
import { toast, ToastContainer } from "react-toastify";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";

const CandidateList = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [candidates, setCandidates] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("All");
  const [deleteModal, setDeleteModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const job = state?.job;
  const [jobId, setJobId] = useState(job?.id || "");

  const sortByName = [
    {
      options: [
        { label: t("candidate_list.filter.all"), value: "All" },
        { label: t("candidate_list.filter.today"), value: "Today" },
        { label: t("candidate_list.filter.yesterday"), value: "Yesterday" },
        { label: t("candidate_list.filter.last_7_days"), value: "Last 7 Days" },
        { label: t("candidate_list.filter.last_30_days"), value: "Last 30 Days" },
        { label: t("candidate_list.filter.this_month"), value: "This Month" },
        { label: t("candidate_list.filter.last_year"), value: "Last Year" },
      ],
    },
  ];

  useEffect(() => {
    if (job && job.id) {
      setStatus("loading");
      api.get(`/carriere/${job.id}/candidatures`, { withCredentials: true })
          .then((response) => {
            setCandidates(response.data);
            setStatus("succeeded");
          })
          .catch((err) => {
            if (err.response?.data?.status === 401 || err.response?.data?.status === 403) {
              handleApiError(err, dispatch, navigate);
            } else {
              setError(err.message);
              setStatus("failed");
              toast.error(t("candidate_list.toasts.fetch_error"));
            }
          });
    }
  }, [job, dispatch, navigate, t]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDateFilter = (option) => {
    setDateFilter(option.value);
  };

  const handleDeleteCandidate = (candidate) => {
    setCandidateToDelete(candidate);
    setDeleteModal(true);
  };

  const confirmDeleteCandidate = async () => {
    if (candidateToDelete) {
      console.log("Deleting candidate with ID:", candidateToDelete,job);

      try {
        await api.delete(`/carriere/candidature/${candidateToDelete._id}`, {
          withCredentials: true,
        });
        setCandidates(candidates.filter((c) => c._id !== candidateToDelete._id));
        if (job) {
          const updatedJob = {
            ...job,
            candidateCount: (job.candidateCount || 0) - 1,
          };
          dispatch(updateJobRequest({ id: job.id, data: updatedJob, dispatch, navigate }));
        }
        setDeleteModal(false);
        toast.success(t("candidate_list.toasts.delete_success"));
      } catch (err) {
        handleApiError(err, dispatch, navigate);
        toast.error(t("candidate_list.toasts.delete_error"));
      }
    }
  };

  const filteredCandidates = useMemo(() => {
    return candidates.filter((candidate) => {
      const matchesSearch =
          candidate.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
          candidate.email.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [candidates, searchQuery]);

  document.title = `Candidate List - ${job?.jobTitle || "Default Job"}`;

  return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <BreadCrumb
                title={t("candidate_list.title", { jobTitle: job?.jobTitle || t("candidate_list.default_job") })}
                pageTitle={t("candidate_list.page_title")}
            />
            <ToastContainer closeButton={false} limit={1} />
            <DeleteModal
                show={deleteModal}
                onDeleteClick={confirmDeleteCandidate}
                onCloseClick={() => setDeleteModal(false)}
            />
            <Row className="g-4 mb-4">
              <Col className="col-sm">
                <div className="d-md-flex justify-content-sm-end gap-2">
                  <div className="search-box ms-md-2 flex-shrink-0 mb-3 mb-md-0">
                    <Input
                        type="text"
                        className="form-control"
                        id="searchCandidate"
                        autoComplete="off"
                        placeholder={t("candidate_list.search_placeholder")}
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                  <Select
                      className="mb-0"
                      options={sortByName}
                      onChange={handleDateFilter}
                      value={sortByName[0].options.find((opt) => opt.value === dateFilter)}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          border: "1px solid rgba(0, 0, 0, 0.15)",
                          borderRadius: "5px",
                        }),
                      }}
                  />
                </div>
              </Col>
            </Row>
            <Row className="gy-2 mb-2" id="candidate-list">
              {status === "loading" ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
              ) : status === "failed" ? (
                  <Alert color="danger">{t("candidate_list.error", { message: error })}</Alert>
              ) : filteredCandidates.length === 0 ? (
                  <div className="py-4 mt-4 text-center" id="noresult">
                    <lord-icon
                        src="https://cdn.lordicon.com/msoeawqm.json"
                        trigger="loop"
                        colors="primary:#405189,secondary:#0ab39c"
                        style={{ width: "72px", height: "72px" }}
                    ></lord-icon>
                    <h5 className="mt-4">{t("candidate_list.no_results")}</h5>
                  </div>
              ) : (
                  filteredCandidates.map((item, key) => (
                      <Col lg={12} key={key}>
                        <Card className="mb-0">
                          <CardBody>
                            <div className="d-lg-flex align-items-center">
                              <div className="ms-3 flex-grow-1">
                                <NavLink to="/pages-profile" state={{ candidate: item, job: job }}>
                                  <h5 className="fs-16 mb-2">{item.nom}</h5>
                                </NavLink>
                                <p className="text-muted mb-0">{item.email}</p>
                              </div>
                              <div>
                                <i className="ri-briefcase-2-line align-bottom me-1"></i>
                                <span>{moment(item?.date).format("YYYY-MM-DD HH:mm") || t("candidate_list.date_na")}</span>
                              </div>
                              <div className="ms-3">
                                <UncontrolledDropdown>
                                  <DropdownToggle tag="a" role="button">
                                    <i className="ri-more-fill fs-17"></i>
                                  </DropdownToggle>
                                  <DropdownMenu>
                                    <NavLink
                                        to="/pages-profile"
                                        state={{ candidate: item, job: job }}
                                        className="dropdown-item"
                                    >
                                      <i className="ri-eye-line me-2 align-bottom text-muted"></i>
                                      {t("candidate_list.actions.view")}
                                    </NavLink>
                                    <DropdownItem onClick={() => handleDeleteCandidate(item)}>
                                      <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                                      {t("candidate_list.actions.delete")}
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                  ))
              )}
            </Row>
            <Row className="g-0 justify-content-end mb-4" id="pagination-element">
              <Col sm={6}>
                <div className="pagination-block pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                  <div className="page-item">
                    <Link to="" className="page-link" id="page-prev">
                      {t("candidate_list.pagination.previous")}
                    </Link>
                  </div>
                  <span id="page-num" className="pagination"></span>
                  <div className="page-item">
                    <Link to="" className="page-link" id="page-next">
                      {t("candidate_list.pagination.next")}
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
  );
};

export default CandidateList;